import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { blogService, siteConfigService } from '../services/api';
import { updateMetaTags } from '../utils/seo';
import { Calendar, Tag, User, ChevronLeft, Clock, Eye, Share2, Facebook, Linkedin } from 'lucide-react';
import XLogo from './XLogo';
import AdSlot from './AdSlot';

function BlogDetail() {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    loadBlog();
    loadSettings();
  }, [slug]);

  const loadSettings = async () => {
    try {
      const data = await siteConfigService.settings();
      setSettings(data);
    } catch (e) {
      console.error('Error loading settings:', e);
    }
  };

  const loadBlog = async () => {
    setLoading(true);
    try {
      const data = await blogService.show(slug);
      const blogData = data.data || data;
      setBlog(blogData);
      
      if (blogData) {
        updateMetaTags({
          title: blogData.meta_title || blogData.title,
          description: blogData.meta_description || blogData.excerpt,
          image: blogData.featured_image,
          siteName: settings?.site_name || 'Ortus FMCG'
        });
      }
    } catch (error) {
      console.error('Error loading blog detail:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="flex flex-col items-center gap-4">
        <div className="w-12 h-12 border-4 border-red-500 border-t-transparent rounded-full animate-spin"></div>
        <p className="text-slate-500 font-medium text-sm">Loading article...</p>
      </div>
    </div>
  );

  if (!blog) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-slate-900 mb-4">Article Not Found</h2>
        <Link to="/blog" className="text-red-600 font-bold uppercase tracking-wider text-xs flex items-center justify-center gap-2">
          <ChevronLeft size={16} /> Back to Blog
        </Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-white">
      {/* Article Header - Colorful Gradient */}
      <header className="bg-gradient-to-br from-slate-900 via-red-900 to-slate-900 text-white pt-24 pb-16 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-red-500 opacity-20 blur-3xl rounded-full"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-500 opacity-10 blur-3xl rounded-full"></div>
        <div className="max-w-4xl mx-auto px-4 relative z-10">
          <Link 
            to="/blog" 
            className="inline-flex items-center gap-2 text-red-400 font-bold uppercase tracking-[0.2em] text-[10px] mb-8 hover:text-red-300 transition-colors"
          >
            <ChevronLeft size={14} /> Back to Blog
          </Link>
          
          <div className="flex flex-wrap gap-4 mb-6">
            {blog.tags && blog.tags.map(tag => (
              <span key={tag} className="px-3 py-1 rounded-full bg-red-500/10 text-red-400 border border-red-500/20 text-[10px] font-black uppercase tracking-widest">
                #{tag}
              </span>
            ))}
          </div>

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-black mb-8 leading-tight">
            {blog.title}
          </h1>

          <div className="flex flex-wrap items-center gap-6 text-sm text-slate-400">
            <div className="flex items-center gap-2">
              <Calendar size={16} className="text-red-500" />
              {new Date(blog.created_at).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })}
            </div>
            {blog.author && (
              <div className="flex items-center gap-2">
                <User size={16} className="text-red-500" />
                <span>By {blog.author.name}</span>
              </div>
            )}
            <div className="flex items-center gap-2">
              <Eye size={16} className="text-red-500" />
              <span>{blog.views || 0} Views</span>
            </div>
          </div>
        </div>
      </header>

      {/* Featured Image */}
      {blog.featured_image && (
        <div className="max-w-6xl mx-auto px-4 -mt-12 relative z-20">
          <div className="aspect-[21/9] rounded-3xl overflow-hidden shadow-2xl border-4 border-white">
            <img 
              src={blog.featured_image} 
              alt={blog.title} 
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      )}

      {/* Article Content */}
      <main className="max-w-4xl mx-auto px-4 py-20">
        <div className="flex flex-col md:flex-row gap-12">
          {/* Main Content */}
          <div className="flex-1 min-w-0">
            {blog.excerpt && (
              <div className="text-xl text-slate-500 italic mb-10 border-l-4 border-red-500 pl-6 leading-relaxed">
                {blog.excerpt}
              </div>
            )}
            
            <div 
              className="prose prose-lg max-w-none text-slate-700 leading-relaxed
                prose-headings:text-slate-900 prose-headings:font-black
                prose-a:text-red-600 prose-a:font-bold prose-a:no-underline hover:prose-a:underline
                prose-img:rounded-3xl prose-img:shadow-lg
                prose-blockquote:border-red-500 prose-blockquote:bg-slate-50 prose-blockquote:py-2 prose-blockquote:px-6 prose-blockquote:rounded-r-2xl prose-blockquote:not-italic
                prose-strong:text-slate-900 prose-strong:font-black"
              dangerouslySetInnerHTML={{ __html: blog.content }} 
            />

            {/* Tags Bottom */}
            {blog.tags && blog.tags.length > 0 && (
              <div className="mt-16 pt-8 border-t border-slate-100">
                <p className="text-xs font-black uppercase tracking-widest text-slate-400 mb-4">Tags</p>
                <div className="flex flex-wrap gap-2">
                  {blog.tags.map(tag => (
                    <span key={tag} className="px-4 py-2 rounded-xl bg-slate-50 text-slate-600 text-xs font-bold hover:bg-red-50 hover:text-red-600 transition-colors cursor-pointer">
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <aside className="w-full md:w-64 shrink-0 space-y-8">
            {/* Share */}
            <div className="bg-slate-50 rounded-3xl p-6 border border-slate-100">
              <h4 className="text-sm font-black uppercase tracking-widest text-slate-900 mb-6 flex items-center gap-2">
                <Share2 size={16} className="text-red-600" /> Share Post
              </h4>
              <div className="flex flex-col gap-3">
                <button className="flex items-center gap-3 w-full p-3 rounded-xl bg-white border border-slate-200 text-slate-600 hover:border-red-500 hover:text-red-600 transition-all text-sm font-bold">
                  <Facebook size={18} /> Facebook
                </button>
                <button className="flex items-center gap-3 w-full p-3 rounded-xl bg-white border border-slate-200 text-slate-600 hover:border-red-500 hover:text-red-600 transition-all text-sm font-bold">
                  <XLogo size={18} /> X (Twitter)
                </button>
                <button className="flex items-center gap-3 w-full p-3 rounded-xl bg-white border border-slate-200 text-slate-600 hover:border-red-500 hover:text-red-600 transition-all text-sm font-bold">
                  <Linkedin size={18} /> LinkedIn
                </button>
              </div>
            </div>
            
            {/* Sidebar Ad Slot */}
            <AdSlot position="blog_sidebar" className="min-h-[400px]" />

            {/* Related/Newsletter Placeholder */}
            <div className="bg-slate-900 rounded-3xl p-6 text-white overflow-hidden relative">
              <div className="absolute top-0 right-0 w-32 h-32 bg-red-600 opacity-20 blur-3xl -translate-y-1/2 translate-x-1/2 rounded-full"></div>
              <h4 className="text-sm font-black uppercase tracking-widest text-red-400 mb-4">Subscribe</h4>
              <p className="text-xs text-slate-400 mb-6 leading-relaxed">Get the latest insights delivered straight to your inbox.</p>
              <input 
                type="email" 
                placeholder="Email address"
                className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-xs focus:outline-none focus:border-red-500 transition-colors mb-3"
              />
              <button className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-3 rounded-xl text-xs transition shadow-lg shadow-red-900/40">
                Join Newsletter
              </button>
            </div>
          </aside>
        </div>
      </main>
    </div>
  );
}

export default BlogDetail;
